import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, XIcon, CheckCircleIcon, XCircleIcon, MoreVerticalIcon, AlertCircleIcon, UserCheckIcon, EditIcon, SaveIcon, InfoIcon, UserPlusIcon, MailIcon, UploadIcon, ArchiveIcon, ShieldAlertIcon, ClockIcon, ShieldIcon, MailCheckIcon, ActivityIcon, BookOpenIcon, GraduationCapIcon, CalendarIcon, TrashIcon, DownloadIcon, Loader2Icon, PlusIcon, FileTextIcon, GridIcon, ListIcon, AlertTriangleIcon, RotateCcwIcon, UserXIcon, GripVerticalIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MemberProfilePanel } from '../components/MemberProfilePanel';
import { ArchiveEducatorModal } from '../components/ArchiveEducatorModal';
import { RestoreEducatorModal } from '../components/RestoreEducatorModal';
import { RemoveFromOrgModal } from '../components/RemoveFromOrgModal';
import { TransferPrimaryAdminModal } from '../components/TransferPrimaryAdminModal';
// --- Types ---
type Role = 'Primary Admin' | 'Secondary Admin' | 'Educator';
type MemberStatus = 'Active' | 'Archived';
type JoinMethod = 'invited' | 'requested';
interface Member {
  id: number;
  name: string;
  email: string;
  role: Role;
  emailVerified: boolean;
  status: MemberStatus;
  lastActive: string;
  lastActiveDate?: string;
  avatar?: string;
  addedBy: string;
  addedOn: string;
  addedOnDate?: string;
  approvedBy: string;
  approvedOn: string;
  joinMethod: JoinMethod;
  archivedBy?: string;
  archivedOn?: string;
  archivedReason?: string;
  removedBy?: string;
  removedOn?: string;
  dataRetentionPolicy?: 'keep' | 'delete';
  grades?: string[];
  subjects?: string[];
}
interface Request {
  id: number;
  name: string;
  email: string;
  requestedOn: string;
  requestedOnDate?: string;
  emailVerified: boolean;
  grades?: string[];
  subjects?: string[];
}
// --- Helper Components ---
const LoadingSpinner = ({
  size = 16,
  className = ''
}: {
  size?: number;
  className?: string;
}) => <Loader2Icon size={size} className={`animate-spin ${className}`} data-id="element-3079" />;
const MemberTableSkeleton = () => <div className="animate-pulse space-y-4" data-id="element-3080">
    {[1, 2, 3, 4, 5].map(i => <div key={i} className="flex items-center space-x-4 p-4 bg-surface border border-custom rounded-xl" data-id="element-3081">
        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" data-id="element-3082" />
        <div className="flex-1 space-y-2" data-id="element-3083">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" data-id="element-3084" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" data-id="element-3085" />
        </div>
        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded" data-id="element-3086" />
      </div>)}
  </div>;
const MemberCard = ({
  member,
  onClick,
  onArchive,
  canArchive
}: {
  member: Member;
  onClick: () => void;
  onArchive: (e: React.MouseEvent) => void;
  canArchive: boolean;
}) => <div onClick={onClick} className="bg-surface border border-custom rounded-xl p-4 space-y-4 hover:border-[#00B75F] transition-colors cursor-pointer" data-id="element-3087">
    <div className="flex items-start justify-between" data-id="element-3088">
      <div className="flex items-center gap-3" data-id="element-3089">
        <div className="w-10 h-10 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-3090">
          {member.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div data-id="element-3091">
          <h3 className="font-bold text-text-primary" data-id="element-3092">{member.name}</h3>
          <p className="text-xs text-text-secondary" data-id="element-3093">{member.email}</p>
        </div>
      </div>
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${member.role === 'Primary Admin' ? 'bg-[#2563EB] bg-opacity-10 text-[#2563EB]' : member.role === 'Secondary Admin' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'bg-surface border border-custom text-text-primary'}`} data-id="element-3094">
        {member.role}
      </span>
    </div>

    <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3095">
      <div className="flex flex-col" data-id="element-3096">
        <span className="text-text-tertiary" data-id="element-3097">Status</span>
        <span className={`font-medium ${member.status === 'Active' ? 'text-[#00B75F]' : 'text-text-tertiary'}`} data-id="element-3098">
          {member.status}
        </span>
      </div>
      <div className="flex flex-col" data-id="element-3099">
        <span className="text-text-tertiary" data-id="element-3100">Last Active</span>
        <span className="text-text-primary" data-id="element-3101">{member.lastActive}</span>
      </div>
    </div>

    {member.role === 'Educator' && (member.grades || member.subjects) && <div className="flex flex-wrap gap-1" data-id="element-3102">
        {member.grades?.slice(0, 2).map((g, i) => <span key={i} className="px-2 py-0.5 bg-surface-elevated rounded text-xs text-text-secondary border border-custom" data-id="element-3103">
            {g}
          </span>)}
        {member.subjects?.slice(0, 2).map((s, i) => <span key={i} className="px-2 py-0.5 bg-surface-elevated rounded text-xs text-text-secondary border border-custom" data-id="element-3104">
            {s}
          </span>)}
      </div>}
  </div>;
const RequestCard = ({
  request,
  onApprove,
  onDecline
}: {
  request: Request;
  onApprove: () => void;
  onDecline: () => void;
}) => <div className="bg-surface border border-custom rounded-xl p-4 space-y-4 hover:border-[#00B75F] transition-colors" data-id="element-3105">
    <div className="flex items-start justify-between" data-id="element-3106">
      <div className="flex items-center gap-3" data-id="element-3107">
        <div className="w-10 h-10 rounded-full bg-[#2563EB] bg-opacity-10 text-[#2563EB] flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-3108">
          {request.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div data-id="element-3109">
          <h3 className="font-bold text-text-primary" data-id="element-3110">{request.name}</h3>
          <p className="text-xs text-text-secondary" data-id="element-3111">{request.email}</p>
        </div>
      </div>
      {request.emailVerified ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap bg-[#00B75F] bg-opacity-10 text-[#00B75F]" data-id="element-3112">
          <CheckCircleIcon size={12} data-id="element-3113" /> Verified
        </span> : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]" data-id="element-3114">
          <AlertCircleIcon size={12} data-id="element-3115" /> Pending
        </span>}
    </div>

    <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3116">
      <div className="flex flex-col" data-id="element-3117">
        <span className="text-text-tertiary" data-id="element-3118">Requested</span>
        <span className="text-text-primary" data-id="element-3119">{request.requestedOn}</span>
      </div>
      <div className="flex flex-col" data-id="element-3120">
        <span className="text-text-tertiary" data-id="element-3121">Teaching Context</span>
        {request.grades || request.subjects ? <div className="flex flex-wrap gap-1 mt-1" data-id="element-3122">
            {request.grades?.slice(0, 1).map((g, i) => <span key={i} className="px-1.5 py-0.5 bg-surface-elevated rounded text-xs text-text-secondary border border-custom" data-id="element-3123">
                {g}
              </span>)}
            {request.subjects?.slice(0, 1).map((s, i) => <span key={i} className="px-1.5 py-0.5 bg-surface-elevated rounded text-xs text-text-secondary border border-custom" data-id="element-3124">
                {s}
              </span>)}
          </div> : <span className="text-text-tertiary" data-id="element-3125">—</span>}
      </div>
    </div>

    <div className="flex gap-2 pt-2" data-id="element-3126">
      <button onClick={onApprove} className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#00B75F] hover:bg-[#00a055] rounded-lg transition-colors" data-id="element-3127">
        Approve
      </button>
      <button onClick={onDecline} className="flex-1 px-4 py-2 text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors border border-custom" data-id="element-3128">
        Decline
      </button>
    </div>
  </div>;
// --- Main Component ---
export function OrganizationMembers() {
  // State declarations
  const [activeTab, setActiveTab] = useState<'members' | 'requests'>('members');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [requestViewMode, setRequestViewMode] = useState<'table' | 'cards'>('table');
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [lastActiveFilter, setLastActiveFilter] = useState<'all' | 'inactive-30-days' | 'inactive-60-days' | 'inactive-90-days'>('all');
  // Sorting State
  type SortField = 'name' | 'email' | 'role' | 'status' | 'lastActive';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('role');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // Requests Tab
  const [requestSearchQuery, setRequestSearchQuery] = useState('');
  const [requestSortBy, setRequestSortBy] = useState<'date-desc' | 'date-asc' | 'name'>('date-desc');
  // Modals & Panels
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState<Request | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role>('Educator');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMode, setInviteMode] = useState<'single' | 'bulk'>('single');
  const [singleEmail, setSingleEmail] = useState('');
  const [singleRole, setSingleRole] = useState<Role>('Educator');
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkRole, setBulkRole] = useState<Role>('Educator');
  const [showMemberPanel, setShowMemberPanel] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [editedRole, setEditedRole] = useState<Role | null>(null);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [requestToDecline, setRequestToDecline] = useState<Request | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showTransferAdminModal, setShowTransferAdminModal] = useState(false);
  const [newPrimaryAdminId, setNewPrimaryAdminId] = useState<number | ''>('');
  const [transferConfirmed, setTransferConfirmed] = useState(false);
  const [showPrimaryAdminConfirm, setShowPrimaryAdminConfirm] = useState(false);
  // Education Editing
  const [isEditingEducation, setIsEditingEducation] = useState(false);
  const [editedGrades, setEditedGrades] = useState<string[]>([]);
  const [editedSubjects, setEditedSubjects] = useState<string[]>([]);
  // Loading States
  const [isApprovingRequest, setIsApprovingRequest] = useState(false);
  const [isDecliningRequest, setIsDecliningRequest] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'info'>('success');
  // Archive/Restore/Remove modals
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showRemoveFromOrgModal, setShowRemoveFromOrgModal] = useState(false);
  const [showTransferPrimaryAdminModal, setShowTransferPrimaryAdminModal] = useState(false);
  const [memberToArchive, setMemberToArchive] = useState<Member | null>(null);
  const [memberToRestore, setMemberToRestore] = useState<Member | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [memberToTransfer, setMemberToTransfer] = useState<Member | null>(null);
  const currentUserRole: Role = 'Primary Admin';
  const currentUserName = 'John Smith';
  // Column resize state
  const [columnWidths, setColumnWidths] = useState({
    name: 220,
    email: 200,
    role: 140,
    context: 180,
    status: 100,
    lastActive: 120
  });
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const tableRef = useRef<HTMLTableElement>(null);
  // Column resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(column);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[column as keyof typeof columnWidths];
  }, [columnWidths]);
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const diff = e.clientX - resizeStartX.current;
    const newWidth = Math.max(80, resizeStartWidth.current + diff);
    setColumnWidths(prev => ({
      ...prev,
      [isResizing]: newWidth
    }));
  }, [isResizing]);
  const handleResizeEnd = useCallback(() => {
    setIsResizing(null);
  }, []);
  // Add/remove resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  // Mock Data
  const [members, setMembers] = useState<Member[]>([{
    id: 1,
    name: 'John Smith',
    email: 'john.smith@school.edu',
    role: 'Primary Admin',
    emailVerified: true,
    status: 'Active',
    lastActive: '2 hours ago',
    lastActiveDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    addedBy: 'System',
    addedOn: 'Jan 15, 2024',
    addedOnDate: '2024-01-15',
    approvedBy: 'System',
    approvedOn: 'Jan 15, 2024',
    joinMethod: 'invited'
  }, {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@school.edu',
    role: 'Secondary Admin',
    emailVerified: true,
    status: 'Active',
    lastActive: '1 day ago',
    lastActiveDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    addedBy: 'John Smith',
    addedOn: 'Feb 10, 2024',
    addedOnDate: '2024-02-10',
    approvedBy: 'John Smith',
    approvedOn: 'Feb 10, 2024',
    joinMethod: 'invited'
  }, {
    id: 3,
    name: 'Michael Chen',
    email: 'mchen@school.edu',
    role: 'Educator',
    emailVerified: true,
    status: 'Active',
    lastActive: '3 days ago',
    lastActiveDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    addedBy: 'Sarah Johnson',
    addedOn: 'Mar 5, 2024',
    addedOnDate: '2024-03-05',
    approvedBy: 'John Smith',
    approvedOn: 'Mar 5, 2024',
    joinMethod: 'requested',
    grades: ['Grade 9', 'Grade 10'],
    subjects: ['Physics', 'Mathematics']
  }]);
  const [requests, setRequests] = useState<Request[]>([{
    id: 1,
    name: 'Jessica Martinez',
    email: 'jessica.m@school.edu',
    requestedOn: '2 days ago',
    requestedOnDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    emailVerified: true,
    grades: ['Grade 10', 'Grade 11'],
    subjects: ['Spanish', 'French']
  }]);
  // Effects
  useEffect(() => {
    const timer = setTimeout(() => setIsLoadingMembers(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('cards');
        setRequestViewMode('cards');
      } else {
        setViewMode('table');
        setRequestViewMode('table');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Helpers
  const showToastNotification = (message: string, type: 'success' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };
  const allGrades = Array.from(new Set(members.filter(m => m.grades).flatMap(m => m.grades || []))).sort();
  const allSubjects = Array.from(new Set(members.filter(m => m.subjects).flatMap(m => m.subjects || []))).sort();
  const getSecondaryAdminCount = () => {
    return members.filter(m => m.role === 'Secondary Admin' && m.status === 'Active').length;
  };
  const shouldWarnAdminCount = () => {
    return getSecondaryAdminCount() >= 6;
  };
  // Filtering Logic
  const filteredMembers = members.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) || member.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && member.status === 'Active' || statusFilter === 'archived' && member.status === 'Archived';
    const matchesGrade = gradeFilter === 'all' || member.grades && member.grades.includes(gradeFilter);
    const matchesSubject = subjectFilter === 'all' || member.subjects && member.subjects.includes(subjectFilter);
    let matchesLastActive = true;
    if (lastActiveFilter !== 'all') {
      if (!member.lastActiveDate) {
        matchesLastActive = true;
      } else {
        const activeDate = new Date(member.lastActiveDate);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - activeDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (lastActiveFilter === 'inactive-30-days') matchesLastActive = diffDays > 30;
        if (lastActiveFilter === 'inactive-60-days') matchesLastActive = diffDays > 60;
        if (lastActiveFilter === 'inactive-90-days') matchesLastActive = diffDays > 90;
      }
    }
    return matchesSearch && matchesRole && matchesStatus && matchesGrade && matchesSubject && matchesLastActive;
  });
  // Sorting Logic
  const getRolePriority = (role: Role): number => {
    switch (role) {
      case 'Primary Admin':
        return 1;
      case 'Secondary Admin':
        return 2;
      case 'Educator':
        return 3;
      default:
        return 4;
    }
  };
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'email':
        comparison = a.email.localeCompare(b.email);
        break;
      case 'role':
        comparison = getRolePriority(a.role) - getRolePriority(b.role);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'lastActive':
        const dateA = a.lastActiveDate ? new Date(a.lastActiveDate).getTime() : 0;
        const dateB = b.lastActiveDate ? new Date(b.lastActiveDate).getTime() : 0;
        comparison = dateB - dateA; // Most recent first by default
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set default direction
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) {
      return <svg className="w-4 h-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3129">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" data-id="element-3130" />
        </svg>;
    }
    return sortDirection === 'asc' ? <svg className="w-4 h-4 text-[#00B75F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3131">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-3132" />
      </svg> : <svg className="w-4 h-4 text-[#00B75F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3133">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-3134" />
      </svg>;
  };
  const filteredRequests = requests.filter(req => {
    return req.name.toLowerCase().includes(requestSearchQuery.toLowerCase()) || req.email.toLowerCase().includes(requestSearchQuery.toLowerCase());
  }).sort((a, b) => {
    if (requestSortBy === 'name') return a.name.localeCompare(b.name);
    if (!a.requestedOnDate || !b.requestedOnDate) return 0;
    const dateA = new Date(a.requestedOnDate).getTime();
    const dateB = new Date(b.requestedOnDate).getTime();
    return requestSortBy === 'date-asc' ? dateA - dateB : dateB - dateA;
  });
  // Handlers (keeping all original handlers - abbreviated for space)
  const handleMemberClick = (member: Member) => {
    setSelectedMember(member);
    setEditedRole(member.role);
    setIsEditingRole(false);
    setIsEditingEducation(false);
    setEditedGrades(member.grades || []);
    setEditedSubjects(member.subjects || []);
    setArchiveReason('');
    setShowMemberPanel(true);
  };
  const handleArchiveClick = (member: Member, e: React.MouseEvent) => {
    e.stopPropagation();
    setMemberToArchive(member);
    setShowArchiveModal(true);
  };
  const canEditRole = (member: Member) => {
    if (member.status === 'Archived') return false;
    if (currentUserRole === 'Primary Admin') return true;
    if (currentUserRole === 'Secondary Admin' && member.role === 'Primary Admin') return false;
    return currentUserRole === 'Secondary Admin';
  };
  const canArchiveMember = (member: Member) => {
    if (member.status === 'Archived') return false;
    if (currentUserRole === 'Primary Admin') {
      const activePrimaryAdmins = members.filter(m => m.role === 'Primary Admin' && m.status === 'Active').length;
      if (member.role === 'Primary Admin' && activePrimaryAdmins === 1) {
        return false;
      }
      return true;
    }
    if (currentUserRole === 'Secondary Admin' && member.role === 'Primary Admin') {
      return false;
    }
    return currentUserRole === 'Secondary Admin';
  };
  const getRoleDescription = (role: Role) => {
    switch (role) {
      case 'Educator':
        return 'Creates and uses assessments. Views analytics only for their own classes and students.';
      case 'Secondary Admin':
        return 'Delegated administrator who manages members, approvals, and educator analytics. Cannot change ownership or remove Primary Admin.';
      case 'Primary Admin':
        return 'Ultimate organization owner with full control over members, approvals, analytics, and settings. Can transfer ownership.';
    }
  };
  const handleInviteClick = () => {
    setShowInviteModal(true);
    setInviteMode('single');
    setSingleEmail('');
    setSingleRole('Educator');
    setBulkEmails('');
    setBulkRole('Educator');
  };
  const handleApproveClick = (request: Request) => {
    setRequestToApprove(request);
    setSelectedRole('Educator');
    setShowApproveModal(true);
  };
  const handleDeclineClick = (request: Request) => {
    setRequestToDecline(request);
    setDeclineReason('');
    setShowDeclineModal(true);
  };
  // Change role handler
  const handleChangeRole = (member: Member, newRole: Role) => {
    console.log('Changing role for:', member.id, 'New role:', newRole);
    // Update member role
    setMembers(members.map(m => m.id === member.id ? {
      ...m,
      role: newRole
    } : m));
    showToastNotification(`${member.name}'s role changed to ${newRole}`, 'success');
    // Update selected member if panel is open
    if (selectedMember?.id === member.id) {
      setSelectedMember({
        ...member,
        role: newRole
      });
    }
  };
  // Archive handler (from panel)
  const handleArchiveFromPanel = (member: Member) => {
    setMemberToArchive(member);
    setShowArchiveModal(true);
  };
  // Archive handler
  const handleArchiveConfirm = async (reason: string) => {
    if (!memberToArchive) return;
    console.log('Archiving member:', memberToArchive.id, 'Reason:', reason);
    // Update member status
    setMembers(members.map(m => m.id === memberToArchive.id ? {
      ...m,
      status: 'Archived',
      archivedBy: currentUserName,
      archivedOn: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      }),
      archivedReason: reason || undefined
    } : m));
    showToastNotification(`${memberToArchive.name} status changed to Archived. They remain an organization member.`, 'success');
    setMemberToArchive(null);
    setShowMemberPanel(false);
  };
  // Restore handler
  const handleRestoreClick = (member: Member, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setMemberToRestore(member);
    setShowRestoreModal(true);
  };
  const handleRestoreConfirm = async () => {
    if (!memberToRestore) return;
    console.log('Restoring member:', memberToRestore.id);
    // Update member status
    setMembers(members.map(m => m.id === memberToRestore.id ? {
      ...m,
      status: 'Active',
      archivedBy: undefined,
      archivedOn: undefined,
      archivedReason: undefined
    } : m));
    showToastNotification(`${memberToRestore.name} status changed to Active`, 'success');
    setMemberToRestore(null);
    setShowMemberPanel(false);
  };
  // Remove from organization handler
  const handleRemoveFromOrgClick = (member: Member, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setMemberToRemove(member);
    setShowRemoveFromOrgModal(true);
  };
  const handleRemoveFromOrgConfirm = async (deleteAllData: boolean) => {
    if (!memberToRemove) return;
    console.log('Removing member from organization:', memberToRemove.id, 'Delete data:', deleteAllData);
    // Remove member from list (delinked from organization)
    setMembers(members.filter(m => m.id !== memberToRemove.id));
    const dataMessage = deleteAllData ? "Organization data deleted. Educator's personal reports unchanged." : "Organization data preserved. Educator's personal reports unchanged.";
    showToastNotification(`${memberToRemove.name} delinked from organization. ${dataMessage}`, 'info');
    setMemberToRemove(null);
    setShowMemberPanel(false);
  };
  // Transfer primary admin handler
  const handleTransferPrimaryAdmin = (member: Member) => {
    setMemberToTransfer(member);
    setShowTransferPrimaryAdminModal(true);
  };
  const handleTransferPrimaryAdminConfirm = async (newPrimaryAdminId: number) => {
    if (!memberToTransfer) return;
    console.log('Transferring primary admin from:', memberToTransfer.id, 'to:', newPrimaryAdminId);
    // Update roles: current primary becomes secondary, selected admin becomes primary
    setMembers(members.map(m => {
      if (m.id === memberToTransfer.id) {
        return {
          ...m,
          role: 'Secondary Admin' as Role
        };
      }
      if (m.id === newPrimaryAdminId) {
        return {
          ...m,
          role: 'Primary Admin' as Role
        };
      }
      return m;
    }));
    const newPrimaryAdmin = members.find(m => m.id === newPrimaryAdminId);
    showToastNotification(`Primary Admin role transferred to ${newPrimaryAdmin?.name}. You are now a Secondary Admin.`, 'success');
    setMemberToTransfer(null);
    setShowMemberPanel(false);
  };
  // Get available admins for transfer (excluding current primary admin)
  const availableAdminsForTransfer = members.filter(m => m.status === 'Active' && (m.role === 'Secondary Admin' || m.role === 'Primary Admin') && m.id !== memberToTransfer?.id).map(m => ({
    id: m.id,
    name: m.name,
    email: m.email
  }));
  return <Tooltip.Provider delayDuration={200} data-id="element-3135">
      <div className="flex flex-col min-h-screen bg-background" data-id="element-3136">
        <Header data-id="element-3137" />
        <div className="flex flex-1 overflow-hidden" data-id="element-3138">
          <div className="hidden md:block" data-id="element-3139">
            <Sidebar data-id="element-3140" />
          </div>
          <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3141">
            <main className="flex-1" data-id="element-3142">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-3143">
                {/* Page Header */}
                <div className="mb-6" data-id="element-3144">
                  <div className="flex items-start justify-between gap-4 mb-4" data-id="element-3145">
                    <div className="flex-1" data-id="element-3146">
                      <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2" data-id="element-3147">
                        Members
                      </h1>
                      <p className="text-sm sm:text-base text-text-secondary" data-id="element-3148">
                        Manage who has access to this organization and their
                        roles.
                      </p>
                    </div>

                    {currentUserRole === 'Primary Admin' && activeTab === 'members' && <div className="flex gap-2" data-id="element-3149">
                          <DropdownMenu.Root data-id="element-3150">
                            <DropdownMenu.Trigger asChild data-id="element-3151">
                              <button className="p-2.5 bg-surface border-2 border-custom text-text-primary rounded-xl font-semibold hover:bg-surface-elevated transition-colors" aria-label="Export members" data-id="element-3152">
                                {isExporting ? <Loader2Icon size={20} className="animate-spin" data-id="element-3153" /> : <DownloadIcon size={20} data-id="element-3154" />}
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal data-id="element-3155">
                              <DropdownMenu.Content className="min-w-[160px] bg-surface-elevated rounded-xl border border-custom shadow-custom-lg p-1 z-50" sideOffset={5} align="end" data-id="element-3156">
                                <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" data-id="element-3157">
                                  <FileTextIcon size={16} data-id="element-3158" /> Export as CSV
                                </DropdownMenu.Item>
                                <DropdownMenu.Item className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" data-id="element-3159">
                                  <FileTextIcon size={16} data-id="element-3160" /> Export as PDF
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>

                          <button onClick={handleInviteClick} className="p-2.5 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-colors shadow-sm" aria-label="Invite members" data-id="element-3161">
                            <UserPlusIcon size={20} data-id="element-3162" />
                          </button>
                        </div>}
                  </div>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-custom overflow-x-auto" data-id="element-3163">
                  <div className="flex gap-4 sm:gap-6 min-w-max" data-id="element-3164">
                    <button onClick={() => setActiveTab('members')} className={`pb-3 px-2 font-semibold text-sm whitespace-nowrap transition-colors relative ${activeTab === 'members' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3165">
                      Members ({members.length})
                      {activeTab === 'members' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-3166" />}
                    </button>
                    <button onClick={() => setActiveTab('requests')} className={`pb-3 px-2 font-semibold text-sm whitespace-nowrap transition-colors relative ${activeTab === 'requests' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3167">
                      Requests
                      {requests.length > 0 && <span className="ml-2 px-2 py-0.5 bg-[#FF6CA1] text-white text-xs rounded-full" data-id="element-3168">
                          {requests.length}
                        </span>}
                      {activeTab === 'requests' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-3169" />}
                    </button>
                  </div>
                </div>

                {/* Members Tab Content */}
                {activeTab === 'members' && <>
                    <div className="mb-6 space-y-3" data-id="element-3170">
                      {/* Search & Filters */}
                      <div className="flex flex-col sm:flex-row gap-3" data-id="element-3171">
                        <div className="relative w-full sm:flex-1" data-id="element-3172">
                          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-3173" />
                          <input type="text" placeholder="Search by name or email" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary" data-id="element-3174" />
                        </div>

                        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0" data-id="element-3175">
                          <div className="relative min-w-[120px] sm:min-w-[140px] flex-shrink-0" data-id="element-3176">
                            <FilterIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3177" />
                            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value as 'all' | Role)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3178">
                              <option value="all" data-id="element-3179">All roles</option>
                              <option value="Secondary Admin" data-id="element-3180">
                                Secondary Admins
                              </option>
                              <option value="Educator" data-id="element-3181">Educators</option>
                            </select>
                          </div>

                          <div className="relative min-w-[120px] sm:min-w-[140px] flex-shrink-0" data-id="element-3182">
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="w-full px-3 sm:px-4 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3183">
                              <option value="active" data-id="element-3184">Active</option>
                              <option value="archived" data-id="element-3185">Archived</option>
                              <option value="all" data-id="element-3186">All Status</option>
                            </select>
                          </div>

                          {/* View Mode Toggle (Desktop only) */}
                          <div className="hidden md:flex bg-surface border-2 border-custom rounded-xl p-1" data-id="element-3187">
                            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3188">
                              <ListIcon size={18} data-id="element-3189" />
                            </button>
                            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3190">
                              <GridIcon size={18} data-id="element-3191" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Advanced Filters Row */}
                      <div className="flex flex-col sm:flex-row gap-3" data-id="element-3192">
                        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 flex-1" data-id="element-3193">
                          <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3194">
                            <ClockIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3195" />
                            <select value={lastActiveFilter} onChange={e => setLastActiveFilter(e.target.value as any)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3196">
                              <option value="all" data-id="element-3197">Any Activity</option>
                              <option value="inactive-30-days" data-id="element-3198">
                                Inactive 30+ Days
                              </option>
                              <option value="inactive-60-days" data-id="element-3199">
                                Inactive 60+ Days
                              </option>
                              <option value="inactive-90-days" data-id="element-3200">
                                Inactive 90+ Days
                              </option>
                            </select>
                          </div>

                          <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3201">
                            <GraduationCapIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3202" />
                            <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3203">
                              <option value="all" data-id="element-3204">All Grades</option>
                              {allGrades.map(grade => <option key={grade} value={grade} data-id="element-3205">
                                  {grade}
                                </option>)}
                            </select>
                          </div>

                          <div className="relative min-w-[140px] sm:min-w-[160px] flex-shrink-0" data-id="element-3206">
                            <BookOpenIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3207" />
                            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] text-text-primary appearance-none cursor-pointer" data-id="element-3208">
                              <option value="all" data-id="element-3209">All Subjects</option>
                              {allSubjects.map(subject => <option key={subject} value={subject} data-id="element-3210">
                                  {subject}
                                </option>)}
                            </select>
                          </div>
                        </div>

                        {(gradeFilter !== 'all' || subjectFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'active' || lastActiveFilter !== 'all') && <button onClick={() => {
                      setGradeFilter('all');
                      setSubjectFilter('all');
                      setRoleFilter('all');
                      setStatusFilter('active');
                      setLastActiveFilter('all');
                    }} className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-surface border-2 border-custom rounded-xl transition-colors whitespace-nowrap" data-id="element-3211">
                            Clear Filters
                          </button>}
                      </div>
                    </div>

                    {/* Members List */}
                    {isLoadingMembers ? <MemberTableSkeleton data-id="element-3212" /> : filteredMembers.length === 0 ? <div className="text-center py-12 bg-surface-elevated rounded-xl border border-custom" data-id="element-3213">
                        <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3214">
                          <SearchIcon size={32} className="text-text-tertiary" data-id="element-3215" />
                        </div>
                        <h3 className="text-lg font-bold text-text-primary mb-2" data-id="element-3216">
                          No members found
                        </h3>
                        <p className="text-text-secondary" data-id="element-3217">
                          Try adjusting your filters or search query.
                        </p>
                      </div> : viewMode === 'cards' ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3218">
                        {sortedMembers.map(member => <MemberCard key={member.id} member={member} onClick={() => handleMemberClick(member)} onArchive={e => handleArchiveClick(member, e)} canArchive={canArchiveMember(member)} data-id="element-3219" />)}
                      </div> : <div className="bg-surface-elevated rounded-xl border border-custom overflow-hidden" data-id="element-3220">
                        <div className="overflow-x-auto" data-id="element-3221">
                          <table ref={tableRef} className="w-full" style={{
                      tableLayout: 'fixed'
                    }} data-id="element-3222">
                            <thead className="bg-surface border-b border-custom" data-id="element-3223">
                              <tr data-id="element-3224">
                                <th onClick={() => handleSort('name')} style={{
                            width: columnWidths.name
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-elevated transition-colors group relative" data-id="element-3225">
                                  <div className="flex items-center gap-2" data-id="element-3226">
                                    Name
                                    <SortIcon field="name" data-id="element-3227" />
                                  </div>
                                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#00B75F] hover:bg-opacity-30 transition-colors" onMouseDown={e => handleResizeStart(e, 'name')} onClick={e => e.stopPropagation()} data-id="element-3228">
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3229">
                                      <GripVerticalIcon size={12} className="text-[#00B75F]" data-id="element-3230" />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSort('email')} style={{
                            width: columnWidths.email
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-elevated transition-colors group relative" data-id="element-3231">
                                  <div className="flex items-center gap-2" data-id="element-3232">
                                    Email
                                    <SortIcon field="email" data-id="element-3233" />
                                  </div>
                                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#00B75F] hover:bg-opacity-30 transition-colors" onMouseDown={e => handleResizeStart(e, 'email')} onClick={e => e.stopPropagation()} data-id="element-3234">
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3235">
                                      <GripVerticalIcon size={12} className="text-[#00B75F]" data-id="element-3236" />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSort('role')} style={{
                            width: columnWidths.role
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-elevated transition-colors group relative" data-id="element-3237">
                                  <div className="flex items-center gap-2" data-id="element-3238">
                                    Role
                                    <SortIcon field="role" data-id="element-3239" />
                                    {sortField === 'role' && <span className="text-[10px] font-medium text-[#00B75F] bg-[#00B75F]/10 px-1.5 py-0.5 rounded" data-id="element-3240">
                                        Default
                                      </span>}
                                  </div>
                                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#00B75F] hover:bg-opacity-30 transition-colors" onMouseDown={e => handleResizeStart(e, 'role')} onClick={e => e.stopPropagation()} data-id="element-3241">
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3242">
                                      <GripVerticalIcon size={12} className="text-[#00B75F]" data-id="element-3243" />
                                    </div>
                                  </div>
                                </th>
                                <th style={{
                            width: columnWidths.context
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider relative" data-id="element-3244">
                                  Teaching Context
                                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#00B75F] hover:bg-opacity-30 transition-colors group/resize" onMouseDown={e => handleResizeStart(e, 'context')} onClick={e => e.stopPropagation()} data-id="element-3245">
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/resize:opacity-100 transition-opacity" data-id="element-3246">
                                      <GripVerticalIcon size={12} className="text-[#00B75F]" data-id="element-3247" />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSort('status')} style={{
                            width: columnWidths.status
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-elevated transition-colors group relative" data-id="element-3248">
                                  <div className="flex items-center gap-2" data-id="element-3249">
                                    Status
                                    <SortIcon field="status" data-id="element-3250" />
                                  </div>
                                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#00B75F] hover:bg-opacity-30 transition-colors" onMouseDown={e => handleResizeStart(e, 'status')} onClick={e => e.stopPropagation()} data-id="element-3251">
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3252">
                                      <GripVerticalIcon size={12} className="text-[#00B75F]" data-id="element-3253" />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSort('lastActive')} style={{
                            width: columnWidths.lastActive
                          }} className="text-left px-6 py-4 text-xs font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-elevated transition-colors group" data-id="element-3254">
                                  <div className="flex items-center gap-2" data-id="element-3255">
                                    Last Active
                                    <SortIcon field="lastActive" data-id="element-3256" />
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-custom" data-id="element-3257">
                              {sortedMembers.map(member => <tr key={member.id} onClick={() => handleMemberClick(member)} className="hover:bg-surface transition-colors cursor-pointer" data-id="element-3258">
                                  <td className="px-6 py-4" style={{
                            width: columnWidths.name
                          }} data-id="element-3259">
                                    <div className="flex items-center gap-3" data-id="element-3260">
                                      <div className="w-10 h-10 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-3261">
                                        {member.name.split(' ').map(n => n[0]).join('')}
                                      </div>
                                      <span className="font-medium text-text-primary truncate" data-id="element-3262">
                                        {member.name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-text-secondary truncate" style={{
                            width: columnWidths.email
                          }} data-id="element-3263">
                                    {member.email}
                                  </td>
                                  <td className="px-6 py-4" style={{
                            width: columnWidths.role
                          }} data-id="element-3264">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold whitespace-nowrap ${member.role === 'Primary Admin' ? 'bg-[#2563EB] bg-opacity-10 text-[#2563EB]' : member.role === 'Secondary Admin' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'bg-surface border border-custom text-text-primary'}`} data-id="element-3265">
                                      {member.role}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4" style={{
                            width: columnWidths.context
                          }} data-id="element-3266">
                                    {member.role === 'Educator' && (member.grades || member.subjects) ? <div className="flex flex-wrap gap-1 max-w-xs" data-id="element-3267">
                                        {member.grades?.slice(0, 2).map((grade, idx) => <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary" data-id="element-3268">
                                              {grade}
                                            </span>)}
                                        {member.subjects?.slice(0, 2).map((subject, idx) => <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary" data-id="element-3269">
                                              {subject}
                                            </span>)}
                                        {(member.grades?.length || 0) + (member.subjects?.length || 0) > 4 && <span className="inline-flex items-center px-2 py-0.5 bg-surface rounded text-xs font-medium text-text-tertiary" data-id="element-3270">
                                            +
                                            {(member.grades?.length || 0) + (member.subjects?.length || 0) - 4}
                                          </span>}
                                      </div> : <span className="text-sm text-text-tertiary" data-id="element-3271">
                                        —
                                      </span>}
                                  </td>
                                  <td className="px-6 py-4" style={{
                            width: columnWidths.status
                          }} data-id="element-3272">
                                    {member.status === 'Active' ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00B75F] whitespace-nowrap" data-id="element-3273">
                                        <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-3274" />
                                        Active
                                      </span> : <span className="inline-flex items-center gap-1.5 text-sm font-medium text-text-tertiary whitespace-nowrap" data-id="element-3275">
                                        <ArchiveIcon size={16} data-id="element-3276" />
                                        Archived
                                      </span>}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-text-secondary whitespace-nowrap" style={{
                            width: columnWidths.lastActive
                          }} data-id="element-3277">
                                    {member.lastActive}
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>}
                  </>}
              </div>
            </main>
            <Footer data-id="element-3278" />
          </div>
        </div>

        {/* Toast Notification */}
        <AnimatePresence data-id="element-3279">
          {showToast && <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 50
        }} className="fixed bottom-6 right-6 z-[100]" data-id="element-3280">
              <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border ${toastType === 'success' ? 'bg-[#00B75F] border-[#00a055]' : 'bg-[#2563EB] border-[#1d4ed8]'}`} data-id="element-3281">
                {toastType === 'success' ? <CheckCircleIcon size={24} className="text-white flex-shrink-0" data-id="element-3282" /> : <InfoIcon size={24} className="text-white flex-shrink-0" data-id="element-3283" />}
                <p className="text-white font-medium text-sm max-w-md" data-id="element-3284">
                  {toastMessage}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Member Profile Panel */}
        <MemberProfilePanel isOpen={showMemberPanel} member={selectedMember} onClose={() => setShowMemberPanel(false)} onRestore={handleRestoreClick} onRemoveFromOrg={handleRemoveFromOrgClick} onArchive={handleArchiveFromPanel} onChangeRole={handleChangeRole} onTransferPrimaryAdmin={handleTransferPrimaryAdmin} currentUserRole={currentUserRole} data-id="element-3285" />

        {/* Archive Modal */}
        {memberToArchive && <ArchiveEducatorModal isOpen={showArchiveModal} onClose={() => {
        setShowArchiveModal(false);
        setMemberToArchive(null);
      }} onConfirm={handleArchiveConfirm} educatorName={memberToArchive.name} data-id="element-3286" />}

        {/* Restore Modal */}
        {memberToRestore && <RestoreEducatorModal isOpen={showRestoreModal} onClose={() => {
        setShowRestoreModal(false);
        setMemberToRestore(null);
      }} onConfirm={handleRestoreConfirm} educatorName={memberToRestore.name} archivedOn={memberToRestore.archivedOn} archivedReason={memberToRestore.archivedReason} data-id="element-3287" />}

        {/* Remove from Organization Modal */}
        {memberToRemove && <RemoveFromOrgModal isOpen={showRemoveFromOrgModal} onClose={() => {
        setShowRemoveFromOrgModal(false);
        setMemberToRemove(null);
      }} onConfirm={handleRemoveFromOrgConfirm} educatorName={memberToRemove.name} data-id="element-3288" />}

        {/* Transfer Primary Admin Modal */}
        {memberToTransfer && <TransferPrimaryAdminModal isOpen={showTransferPrimaryAdminModal} onClose={() => {
        setShowTransferPrimaryAdminModal(false);
        setMemberToTransfer(null);
      }} onConfirm={handleTransferPrimaryAdminConfirm} currentPrimaryAdmin={memberToTransfer.name} availableAdmins={availableAdminsForTransfer} data-id="element-3289" />}
      </div>
    </Tooltip.Provider>;
}